import { useState, useEffect, useCallback } from "react";
import { BrowserRouter, Routes, Route, Navigate, useParams, useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ShieldAlert } from "lucide-react";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/auth";
import { HeaderBrand } from "@/components/HeaderBrand";
import { StarRating } from "@/components/StarRating";
import { PositiveOutcome } from "@/components/PositiveOutcome";
import { NegativeOutcome } from "@/components/NegativeOutcome";
import { ThankYou } from "@/components/ThankYou";
import { DemoToolbar } from "@/components/DemoToolbar";
import AdminDashboard from "@/pages/AdminDashboard";
import SuperAdmin from "@/pages/SuperAdmin";
import Login from "@/pages/Login";
import EmailAccess from "@/pages/EmailAccess";
import SupportRedirect from "@/pages/SupportRedirect";
import { getRestaurant } from "@/api";
import { getBrand, brandVars } from "@/brand";

const RevealLine = ({ children, delay = 0 }) => (
  <span className="block overflow-hidden pb-1">
    <motion.span
      className="block"
      initial={{ y: "110%" }}
      animate={{ y: 0 }}
      transition={{ duration: 0.7, delay, ease: [0.22, 1, 0.36, 1] }}
    >
      {children}
    </motion.span>
  </span>
);

const RatingScreen = ({ rating, onSelect, starColor }) => (
  <motion.div
    key="rating"
    initial={{ opacity: 0 }}
    animate={{ opacity: 1 }}
    exit={{ opacity: 0, y: -12 }}
    transition={{ duration: 0.3 }}
    className="w-full flex flex-col items-center text-center gap-10"
    data-testid="rating-screen"
  >
    <h1
      data-testid="rating-heading"
      className="font-serif-display text-4xl sm:text-5xl font-medium tracking-tight text-[#1C1917] leading-[1.15]"
    >
      <RevealLine delay={0.2}>¿Qué tal ha sido</RevealLine>
      <RevealLine delay={0.32}>
        tu <em style={{ color: "var(--brand-accent)" }}>experiencia</em>?
      </RevealLine>
    </h1>

    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.55, duration: 0.5, ease: "easeOut" }}
      className="w-full flex flex-col items-center gap-10"
    >
      <StarRating selected={rating} onSelect={onSelect} starColor={starColor} />
      <p className="text-sm text-[#57534E] leading-relaxed max-w-[17rem]">
        Su opinión nos ayuda a mantener la excelencia. ¿Qué tal su visita?.
      </p>
    </motion.div>
  </motion.div>
);

const Shell = ({ children, style }) => (
  <div
    className="relative min-h-screen bg-[#FDFBF7] text-[#1C1917] overflow-hidden"
    style={style}
  >
    <div className="ambient-glow" />
    <div className="grain-overlay" />
    <div className="relative z-10 max-w-md mx-auto min-h-screen px-5 py-6 sm:py-8 flex flex-col">
      {children}
    </div>
  </div>
);

const CenteredMessage = ({ title, subtitle, testId }) => (
  <main
    className="flex-1 flex flex-col items-center justify-center text-center gap-4"
    data-testid={testId}
  >
    <h1 className="font-serif-display text-3xl font-medium tracking-tight text-[#1C1917]">
      {title}
    </h1>
    {subtitle && <p className="text-sm text-[#57534E] max-w-xs">{subtitle}</p>}
  </main>
);

const FullScreenLoader = () => (
  <Shell>
    <main className="flex-1 flex items-center justify-center">
      <p
        data-testid="auth-loading"
        className="font-mono-label text-[10px] uppercase tracking-[0.3em] text-[#A8A29E] animate-pulse"
      >
        Comprobando sesión...
      </p>
    </main>
  </Shell>
);

const AccessDenied = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  return (
    <Shell>
      <main
        className="flex-1 flex flex-col items-center justify-center text-center gap-5"
        data-testid="access-denied"
      >
        <ShieldAlert className="h-8 w-8 text-[#C25E38]" />
        <h1 className="font-serif-display text-3xl font-medium tracking-tight text-[#1C1917]">
          Acceso denegado
        </h1>
        <p className="text-sm text-[#57534E] max-w-xs leading-relaxed">
          No tienes permiso para ver esta sección.
        </p>
        <div className="flex flex-wrap justify-center gap-3 pt-2">
          {user?.role === "restaurant_owner" && user?.restaurant_id && (
            <button
              type="button"
              data-testid="go-my-panel-button"
              onClick={() => navigate(`/admin/${user.restaurant_id}`)}
              className="text-sm font-medium rounded-xl px-5 py-3 bg-[#1C1917] text-[#FDFBF7] hover:bg-[#C25E38] transition-colors duration-300 cursor-pointer"
            >
              Ir a mi panel
            </button>
          )}
          <button
            type="button"
            data-testid="denied-logout-button"
            onClick={() => {
              logout();
              navigate("/login", { replace: true });
            }}
            className="text-sm font-medium rounded-xl px-5 py-3 border border-[#E7E2D7] text-[#57534E] hover:border-[#C25E38]/40 transition-colors duration-200 cursor-pointer"
          >
            Cerrar sesión
          </button>
        </div>
      </main>
    </Shell>
  );
};

const RequireAuth = ({ children }) => {
  const { status } = useAuth();
  if (status === "loading") return <FullScreenLoader />;
  if (status === "out") return <Navigate to="/login" replace />;
  return children;
};

const RequireSuperadmin = ({ children }) => {
  const { user } = useAuth();
  if (user?.role !== "superadmin") return <AccessDenied />;
  return children;
};

const RequireRestaurantAccess = ({ children }) => {
  const { slug } = useParams();
  const { user } = useAuth();
  if (user?.role === "superadmin") return children;
  if (user?.role === "restaurant_owner" && user?.restaurant_id === slug)
    return children;
  return <AccessDenied />;
};

function ReviewFlow() {
  const { slug } = useParams();
  const [restaurant, setRestaurant] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [rating, setRating] = useState(null);
  const [stage, setStage] = useState("rating");

  useEffect(() => {
    let active = true;
    setRestaurant(null);
    setNotFound(false);
    getRestaurant(slug)
      .then((r) => active && setRestaurant(r))
      .catch(() => active && setNotFound(true));
    return () => {
      active = false;
    };
  }, [slug]);

  const goToOutcome = useCallback((value) => {
    setRating(value);
    setStage("outcome");
  }, []);

  const handleSelect = useCallback((value) => {
    setRating(value);
    window.setTimeout(() => setStage("outcome"), 500);
  }, []);

  const handleReset = useCallback(() => {
    setRating(null);
    setStage("rating");
  }, []);

  if (notFound) {
    return (
      <Shell>
        <CenteredMessage
          title="Restaurante no encontrado"
          subtitle="Revisa el código QR o el enlace e inténtalo de nuevo."
          testId="restaurant-not-found"
        />
      </Shell>
    );
  }

  if (!restaurant) {
    return (
      <Shell>
        <main className="flex-1 flex items-center justify-center">
          <p
            data-testid="restaurant-loading"
            className="font-mono-label text-[10px] uppercase tracking-[0.3em] text-[#A8A29E] animate-pulse"
          >
            Cargando...
          </p>
        </main>
      </Shell>
    );
  }

  const brand = getBrand(restaurant);

  return (
    <Shell style={brandVars(brand)}>
      <HeaderBrand
        name={brand.name}
        description={brand.description}
        logoUrl={brand.logoUrl}
        accent={brand.accent}
      />

      <main className="flex-1 flex flex-col items-center justify-center py-10 w-full">
        <AnimatePresence mode="wait">
          {stage === "rating" && (
            <RatingScreen
              rating={rating}
              onSelect={handleSelect}
              starColor={brand.star}
            />
          )}
          {stage === "outcome" &&
            (rating >= 4 ? (
              <PositiveOutcome
                key="positive"
                slug={slug}
                rating={rating}
                googleReviewUrl={restaurant.google_review_url}
                onContinue={() => setStage("thanks")}
              />
            ) : (
              <NegativeOutcome
                key="negative"
                slug={slug}
                rating={rating}
                onSubmit={() => setStage("thanks")}
              />
            ))}
          {stage === "thanks" && <ThankYou onReset={handleReset} />}
        </AnimatePresence>
      </main>

      <footer className="flex flex-col items-center gap-4 pb-2">
        <DemoToolbar onSimulate={goToOutcome} onReset={handleReset} />
        <p className="font-mono-label text-[9px] uppercase tracking-[0.25em] text-[#A8A29E]">
          Review QR · {brand.name}
        </p>
      </footer>
    </Shell>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <Toaster position="top-center" richColors />
        <Routes>
          <Route path="/" element={<Navigate to="/review/la-esquina" replace />} />
          <Route path="/review/:slug" element={<ReviewFlow />} />
          <Route path="/login" element={<Login />} />
          <Route path="/email-access/:token" element={<EmailAccess />} />
          <Route path="/t/:supportCode" element={<SupportRedirect />} />
          <Route
            path="/admin"
            element={<Navigate to="/admin/la-esquina" replace />}
          />
          <Route
            path="/admin/:slug"
            element={
              <RequireAuth>
                <RequireRestaurantAccess>
                  <AdminDashboard />
                </RequireRestaurantAccess>
              </RequireAuth>
            }
          />
          <Route
            path="/superadmin"
            element={
              <RequireAuth>
                <RequireSuperadmin>
                  <SuperAdmin />
                </RequireSuperadmin>
              </RequireAuth>
            }
          />
        </Routes>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
